/**
 * ⭐⭐⭐ A COST THAT ATTACHES TO THE FINANCING.
 *
 * Closing costs attach to the PURCHASE. Operating expenses attach to the PROPERTY. The app had
 * a slot for each of those and none for the third thing, so four capital structures in a row had
 * a real dollar figure that could only be mis-filed or dropped:
 *
 *     mechanic             example                                effect
 *     financed into DEBT   SBA 504, $67,000 of CDC/SBA fees       raises the NOTE, not the cash
 *     paid in CASH         SBA 7(a) guaranty fee, $138,125        raises the CASH, not the debt
 *     paid in CASH         Long Beach assumption fee, $40,990     raises the CASH, not the debt
 *     ONGOING, declining   7(a) SBA service fee, $20,625 -> $19,528   reduces CASH FLOW only
 *
 * ⛔ THE BOOLEAN IS THE CONCEPT. Without `financed`, the same number means two opposite things:
 * $67,000 that rides into a debenture and $138,125 that comes out of the buyer's pocket are the
 * same field with the same units and OPPOSITE effects on every return metric.
 *
 * ⭐⭐ THE WHOLE MODULE IS ONE IDENTITY - sources equal uses:
 *
 *     uses    = purchase price + closing costs + financing fee
 *     sources = primary note + secondary note + cash
 *     => total cash required = price + closing + fee - notes
 *
 * When the fee is FINANCED it is added to a note, so it appears on both sides and cancels: the
 * debt rises and the cash does not move. When it is PAID IN CASH there is no matching source and
 * the cash rises by the whole fee. That is why one boolean is enough to express both mechanics,
 * and why neither can be got wrong once every surface reads this one resolver.
 *
 * 🚨 THE EVIDENCE, from Joseph's own delivered analyses (`corpusReconciliation.test.ts`):
 *   - SBA 504:  $5,750,000 + $115,000 closing + $67,000 financed - $5,242,000 of notes = $690,000,
 *     which is his total cash exactly, and his equity of $575,000 is project cost less debt.
 *   - SBA 7(a): $5,750,000 + $115,000 + $138,125 in cash - $5,000,000 = $1,003,125, his exactly.
 *   - Long Beach assumption: his total cash is $2,552,007 where the app read $2,511,017 - the
 *     $40,990 assumption fee, and being light on cash FLATTERS every return metric.
 */

/** Which note a FINANCED fee rides into. */
export type FinancingFeeNote = 'primary' | 'secondary';

export interface FinancingFeeInputs {
  /** The one-time fee on the financing. NULL or 0 means there is none. */
  amount?: number | string | null;
  /**
   * ⛔ TRUE adds the fee to a NOTE; FALSE charges it to CASH at closing. There is no third
   * option and no default that is safe for both, which is why this is stored rather than guessed.
   */
  financed?: boolean | null;
  /**
   * Which note a financed fee rides into. The 504's fees ride on the CDC DEBENTURE, not on the
   * bank first, and the two notes carry different rates and different exit balances - so this
   * is not cosmetic. Defaults to the primary note, and falls back to it when no second note
   * exists, because a fee routed to a note that is not there would vanish and break the identity
   * above without anything on screen changing.
   */
  note?: FinancingFeeNote | null;
  /**
   * ONGOING, as a percent per year of the OUTSTANDING balance at the START of each year.
   * ⛔ NULL means there is none; 0 is a real answer and must not be conflated with it - the same
   * trap `optionalRate()` was written for on `property_tax_growth_rate`, where a field reading
   * "2" arrived as the string `"2"`, failed a `typeof === 'number'` check, and silently fell back.
   */
  servicingFeeRate?: number | string | null;
  /** Whether the deal actually carries a second note, which decides where a financed fee can go. */
  hasSecondaryNote?: boolean;
  /** Whether the deal carries a primary note. An all-cash deal can finance nothing. */
  hasPrimaryNote?: boolean;
}

export interface FinancingFeeEffect {
  /** The one-time fee. ALWAYS a use of funds, whichever way it is paid. 0 when absent. */
  fee: number;
  /** Added to the primary note's amount. 0 unless the fee is financed onto the primary. */
  primaryUplift: number;
  /** Added to the secondary note's amount. 0 unless the fee is financed onto the secondary. */
  secondaryUplift: number;
  /**
   * The cash the fee actually costs at closing: the whole fee when paid in cash, and ZERO when
   * financed, because the note that carries it is a matching source.
   * ⭐ `fee - primaryUplift - secondaryUplift` by construction, so the identity cannot drift.
   */
  cashCost: number;
  /** The ongoing rate as a percent per year, or NULL when there is none. Never 0-for-absent. */
  servicingFeeRate: number | null;
}

const EMPTY: FinancingFeeEffect = {
  fee: 0,
  primaryUplift: 0,
  secondaryUplift: 0,
  cashCost: 0,
  servicingFeeRate: null,
};

/**
 * ⛔ NULL and 0 are different answers. `n()` maps an empty field to 0, and a stated 0% servicing
 * fee is a real underwriting assumption while a blank one is the absence of one.
 */
function optionalPercent(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const parsed = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function positiveAmount(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const parsed = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Resolve the financing fee into the three numbers every pricing path needs, so that no surface
 * re-derives any of them. This is the same argument that replaced `additionalAnnualDebtService`
 * with `secondaryLoan`: a figure passed alongside the inputs it came from is how the two came
 * apart, and the exit paid off one note of two.
 */
export function resolveFinancingFee(input: FinancingFeeInputs): FinancingFeeEffect {
  const fee = positiveAmount(input.amount);
  const servicingFeeRate = optionalPercent(input.servicingFeeRate);
  if (fee <= 0) return { ...EMPTY, servicingFeeRate };

  const hasPrimary = input.hasPrimaryNote !== false;
  const hasSecondary = input.hasSecondaryNote === true;
  // ⛔ An all-cash deal can finance nothing, so the fee is cash whatever the boolean says.
  const financed = input.financed === true && (hasPrimary || hasSecondary);
  if (!financed) return { fee, primaryUplift: 0, secondaryUplift: 0, cashCost: fee, servicingFeeRate };

  // A fee routed to a note that does not exist falls back to the one that does, rather than
  // disappearing - losing it would silently break sources = uses with nothing on screen moving.
  const ontoSecondary = input.note === 'secondary' ? hasSecondary : !hasPrimary && hasSecondary;
  return {
    fee,
    primaryUplift: ontoSecondary ? 0 : fee,
    secondaryUplift: ontoSecondary ? fee : 0,
    cashCost: 0,
    servicingFeeRate,
  };
}

/**
 * The ongoing servicing fee charged in a given hold year.
 *
 * ⛔ IT IS STRUCK ON THE OPENING BALANCE - the balance at the START of the year, which is the
 * balance after `year - 1` payments. Joseph's own 7(a) proves it: $20,625 in year 1 is 0.4125%
 * of the full $5,000,000 note, before a single payment; year 5's $19,528 is struck on the
 * year-4 closing balance, not the year-5 one.
 *
 * ⭐⭐ IT REACHES CASH FLOW AND NOT DSCR, and that is his convention, not a simplification. His
 * 7(a) prints DSCR 0.66x struck on debt service alone while the cash flow line carries the fee.
 * Getting it backwards would move a covenant test. It is not an operating expense either - the
 * property does not pay it - so it never touches NOI and therefore never touches the exit price.
 */
export function servicingFeeForYear(
  openingBalance: number,
  servicingFeeRate: number | null | undefined
): number {
  if (servicingFeeRate === null || servicingFeeRate === undefined) return 0;
  if (!Number.isFinite(servicingFeeRate) || servicingFeeRate <= 0) return 0;
  if (!Number.isFinite(openingBalance) || openingBalance <= 0) return 0;
  return openingBalance * (servicingFeeRate / 100);
}
